import React, {createRef} from 'react';
import {compose} from 'recompose';
import block from 'bem-cn-lite';

import withEnv, {WithEnvProps} from 'hoc/withEnv';
import withLocale, {WithLocaleProps} from 'hoc/withLocale';
import withRouter, {WithRouterProps} from 'hoc/withRouter';
import withUser, {WithUserProps} from 'hoc/withUser';
import withFeatureFlags, {WithFeatureFlagsProps} from 'hoc/withFeatureFlags';
import withServicesData, {WithServicesDataProps} from 'hoc/withServicesData';
import withSolutionsData, {WithSolutionsDataProps} from 'hoc/withSolutionsData';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';

import {getDesktopConfig, getMobileConfig} from 'units/navigation/config/header/index';
import Logo from 'components/Logo/Logo';
import SidebarNavigation from 'units/navigation/components/SidebarNavigation/SidebarNavigation';
import HeaderUser from 'components/HeaderUser/HeaderUser';
import Navigation from 'units/navigation/components/Navigation/Navigation';
import EnterConsoleLink from 'components/EnterConsoleLink/EnterConsoleLink';
import ButtonWithIcon from 'components/Buttons/ButtonWithIcon/ButtonWithIcon';
import SearchSuggest from 'components/SearchSuggest/SearchSuggest';
import {isSearchPage} from 'units/navigation/utils';
import OutsideClick from 'components/OutsideClick/OutsideClick';
import LangSwitch from 'units/navigation/components/LangSwitch/LangSwitch';
import {checkIsAuthorized} from 'utils';

import searchIcon from 'icons/search.svg';
import navigationChevron from '@yandex-data-ui/page-constructor/assets/icons/navigation-chevron.svg';

import './Header.scss';

const b = block('header');

export const PRODUCTS_COUNT = 7;

export type HeaderProps = WithEnvProps &
    WithLocaleProps &
    WithRouterProps &
    WithUserProps &
    WithFeatureFlagsProps &
    WithServicesDataProps &
    WithSolutionsDataProps &
    WithTranslationProps;

interface HeaderState {
    isSearchMode: boolean;
    isSidebarOpened: boolean;
    activeItemIndex: number;
}

class Header extends React.Component<HeaderProps, HeaderState> {
    static getDesktopConfig = (props: HeaderProps) => {
        const {
            locale,
            user: {status},
            env,
            i18n,
            featureFlags,
        } = props;

        return getDesktopConfig({
            env,
            i18n,
            locale,
            featureFlags,
            isAuthorized: checkIsAuthorized(status),
        });
    };

    static getMobileConfig = (headerProps: HeaderProps) => {
        const {locale, env, i18n, featureFlags, servicesData, solutionsData} = headerProps;

        return getMobileConfig({
            env,
            i18n,
            locale,
            featureFlags,
            serviceCategories: servicesData.categories,
            solutionCategories: solutionsData.categories,
        });
    };

    ref = createRef();
    state = {
        isSearchMode: false,
        isSidebarOpened: false,
        activeItemIndex: -1,
    };
    mobileConfig = Header.getMobileConfig(this.props);
    desktopConfig = Header.getDesktopConfig(this.props);

    componentDidMount() {
        this.setState({
            activeItemIndex: this.findActiveItem(),
        });

        this.props.router.events?.on('routeChangeComplete', this.onRouteChange);
        this.props.router.events?.on('hashChangeComplete', this.onRouteChange);
    }

    componentWillUnmount() {
        this.props.router.events?.off('routeChangeComplete', this.onRouteChange);
        this.props.router.events?.off('hashChangeComplete', this.onRouteChange);
    }

    render() {
        const {isSearchMode} = this.state;

        return (
            <header className={b({['search-mode']: isSearchMode})}>
                {this.renderLeft()}
                {this.renderSearchHiddenBlock()}
                {this.renderSearch()}
                {this.renderRight()}
            </header>
        );
    }

    private renderLeft() {
        const {isSearchMode, isSidebarOpened} = this.state;
        const buttonClass = b('menu');

        return (
            <div className={b('left')}>
                {isSearchMode ? (
                    this.renderBackButton(buttonClass)
                ) : (
                    <SidebarNavigation
                        config={this.mobileConfig}
                        className={buttonClass}
                        isOpened={isSidebarOpened}
                        handleOpenedChange={this.onSidebarOpenedChange}
                    />
                )}
                <div className={b('logo')}>
                    <Logo />
                </div>
            </div>
        );
    }

    private renderSearchHiddenBlock() {
        return (
            <div className={b('search-hidden')}>
                {this.renderNavigation()}
                {this.renderLangSwitch()}
            </div>
        );
    }

    private renderNavigation() {
        const {activeItemIndex} = this.state;

        return (
            <Navigation
                className={b('navigation')}
                config={this.desktopConfig}
                activeItemIndex={activeItemIndex}
                onActiveItemChange={this.onActiveItemChange}
            />
        );
    }

    private renderLangSwitch() {
        return (
            <LangSwitch
                metrikaGoal={'HEADER'}
                className={b('lang-switcher')}
                type={'icon'}
                direction={['bottom-start', 'bottom']}
                tooltipClassName={b('lang-tooltip')}
            />
        );
    }

    private renderRight() {
        return (
            <div className={b('right')}>
                {this.renderAction()}
                {this.renderUser()}
            </div>
        );
    }

    private renderAction() {
        return (
            <div className={b('action')}>
                <EnterConsoleLink context="header" size="l" />
            </div>
        );
    }

    private renderUser() {
        return <HeaderUser className={b('user')} />;
    }

    private renderBackButton(className: string) {
        return (
            <ButtonWithIcon
                className={b('back-button', className)}
                onClick={this.closeSearch}
                icon={navigationChevron}
                iconSize={20}
                size="n"
            />
        );
    }

    private renderSearchButton() {
        return (
            <ButtonWithIcon
                className={b('search-button')}
                onClick={this.toggleSearch}
                icon={searchIcon}
                iconSize={16}
                size="xs"
            />
        );
    }

    private renderSearch() {
        const {isSearchMode} = this.state;
        const {router, i18nK} = this.props;

        if (isSearchPage(router)) {
            return null;
        }

        return (
            <OutsideClick className={b('search')} onOutsideClick={this.closeSearch}>
                <SearchSuggest
                    className={b('search-suggest')}
                    containerClass={b('search-suggest-container')}
                    placeholder={i18nK('label_search-placeholder')}
                    query={isSearchMode ? undefined : ''}
                    focused={isSearchMode}
                    onFocus={this.handleSearchSuggestClick}
                />
                {this.renderSearchButton()}
            </OutsideClick>
        );
    }

    private onRouteChange = () => {
        const activeItemIndex = this.findActiveItem();

        this.setState({
            isSidebarOpened: false,
            isSearchMode: false,
            activeItemIndex,
        });
    };

    private findActiveItem = () => {
        const {router} = this.props;

        return this.desktopConfig.findIndex(({url}) => {
            return !url || url === '/' ? false : router.pathname.startsWith(url);
        });
    };

    private onActiveItemChange = (index: number) => {
        this.setState({activeItemIndex: index});
    };

    private onSidebarOpenedChange = (isSidebarOpened: boolean) => {
        this.setState({isSidebarOpened});
    };

    private toggleSearch = () => {
        this.setState({
            isSearchMode: !this.state.isSearchMode,
            isSidebarOpened: false,
        });
    };

    private closeSearch = () => {
        this.setState({isSearchMode: false});
    };

    private handleSearchSuggestClick = () => {
        const {isSearchMode} = this.state;

        if (!isSearchMode) {
            this.setState({isSearchMode: true});
        }
    };
}

export default compose<HeaderProps, {}>(
    withEnv,
    withLocale,
    withRouter,
    withUser,
    withFeatureFlags,
    withServicesData,
    withSolutionsData,
    withTranslation('header'),
)(Header);
